import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { mergeJobsiteLocations, sortJobsiteLocations } from '../../utils/profile';
import {
  AddJobsiteByIdPayload,
  AddJobsitePayload,
  BillingInfoDetails,
  CompanyInfoDetails,
  JobsiteInfoDetailsType,
  JobsitesDataLoadPayload,
  LoadingStatus,
  ManageTeamInfoDetails,
  NotificationInfoDetailsType,
  MessageTypeChannelsUpdatePayload,
  MessageTypeLoadingStatusPayload,
  MessageTypeWithChannels,
  PersonalDetailsType,
  ProfileDataLoadPayload,
  ProfileState,
  UpdateJobsiteDataStatusPayload,
} from '../../types/profile';

const initialState: ProfileState = {
  personalDetails: { data: null, loadingStatus: LoadingStatus.UNSET, isUpdateModalOpen: false },
  companyDetails: { data: null, loadingStatus: LoadingStatus.UNSET },
  billingDetails: { data: null, loadingStatus: LoadingStatus.UNSET },
  jobsiteDetails: {
    data: null,
    loadingStatus: LoadingStatus.UNSET,
    hasData: true,
    allDataFetched: false,
  },
  teamDetails: { data: null, loadingStatus: LoadingStatus.UNSET },
  notificationDetails: { data: null, loadingStatus: LoadingStatus.UNSET },
  messageSettings: { data: null, loadingStatus: LoadingStatus.UNSET },
};

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setPersonalDetails: (state, action: PayloadAction<PersonalDetailsType>) => {
      state.personalDetails.data = action.payload;
      state.personalDetails.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    setPersonalDetailsLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.personalDetails.loadingStatus = action.payload;
    },
    openPersonalDetailsUpdateModal: (state) => {
      state.personalDetails.isUpdateModalOpen = true;
    },
    closePersonalDetailsUpdateModal: (state) => {
      state.personalDetails.isUpdateModalOpen = false;
    },
    setCompanyDetails: (state, action: PayloadAction<CompanyInfoDetails>) => {
      state.companyDetails.data = action.payload;
      state.companyDetails.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    setCompanyDetailsLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.companyDetails.loadingStatus = action.payload;
    },
    setBillingDetails: (state, action: PayloadAction<BillingInfoDetails>) => {
      state.billingDetails.data = action.payload;
      state.billingDetails.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    setBillingDetailsLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.billingDetails.loadingStatus = action.payload;
    },
    setJobsiteDetails: (state, action: PayloadAction<JobsiteInfoDetailsType>) => {
      state.jobsiteDetails.data = action.payload;
      state.jobsiteDetails.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    addJobsiteLocation: (state, action: PayloadAction<AddJobsitePayload>) => {
      state.jobsiteDetails.loadingStatus = LoadingStatus.IN_PROGRESS;

      const { jobsiteLocation, preferredJobsiteLocationId } = action.payload;

      const existingJobsites = state.jobsiteDetails.data;
      const newJobsites = [jobsiteLocation]
        .filter((location) => location.jobsite)
        .map((location) => {
          if (location.zip_code)
            return { ...location, zip_code: location.zip_code.toString().padStart(5, '0') };
          return location;
        });

      const companyJobsiteLocations = sortJobsiteLocations(
        mergeJobsiteLocations(existingJobsites, newJobsites),
        preferredJobsiteLocationId,
      );

      state.jobsiteDetails.data = companyJobsiteLocations;
      state.jobsiteDetails.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    setJobsiteDetailsLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.jobsiteDetails.loadingStatus = action.payload;
    },
    setJobsiteDetailsHasData: (state, action: PayloadAction<boolean>) => {
      state.jobsiteDetails.hasData = action.payload;
    },
    setJobsiteDetailsAllDataFetched: (state, action: PayloadAction<boolean>) => {
      state.jobsiteDetails.allDataFetched = action.payload;
    },
    updateJobsiteDataStatus: (state, action: PayloadAction<UpdateJobsiteDataStatusPayload>) => {
      const { pageSize, newJobsites } = action.payload;
      if (Number(pageSize) > 0 && newJobsites.length === 0) {
        state.jobsiteDetails.allDataFetched = true;
        if (pageSize === 1) state.jobsiteDetails.hasData = false;
      } else {
        state.jobsiteDetails.allDataFetched = false;
      }
    },
    setTeamDetails: (state, action: PayloadAction<ManageTeamInfoDetails>) => {
      state.teamDetails.data = action.payload;
      state.teamDetails.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    setTeamDetailsLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.teamDetails.loadingStatus = action.payload;
    },
    setNotificationDetails: (state, action: PayloadAction<NotificationInfoDetailsType>) => {
      state.notificationDetails.data = action.payload;
      state.notificationDetails.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    setNotificationDetailsLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.notificationDetails.loadingStatus = action.payload;
    },
    setMessageSettings: (state, action: PayloadAction<MessageTypeWithChannels[]>) => {
      state.messageSettings.data = action.payload;
      state.messageSettings.data = action.payload.map((setting) => ({
        ...setting,
        loadingStatus: LoadingStatus.SUCCEEDED,
      }));
      state.messageSettings.loadingStatus = LoadingStatus.SUCCEEDED;
    },
    setMessageSettingsLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.messageSettings.loadingStatus = action.payload;
    },
    setMessageTypeLoadingStatusById: (
      state,
      action: PayloadAction<MessageTypeLoadingStatusPayload>,
    ) => {
      const { message_type_id, loadingStatus } = action.payload;
      if (state.messageSettings.data) {
        const messageType = state.messageSettings.data.find(
          (type) => type.message_type_id === message_type_id,
        );
        if (messageType) {
          messageType.loadingStatus = loadingStatus;
        }
      }
    },
    updateMessageTypeChannelsById: (
      state,
      action: PayloadAction<MessageTypeChannelsUpdatePayload>,
    ) => {
      const { message_type_id, active_channels } = action.payload;
      if (state.messageSettings.data) {
        const messageType = state.messageSettings.data.find(
          (type) => type.message_type_id === message_type_id,
        );
        if (messageType) {
          // Create a set of active channel IDs
          const activeChannelIds = new Set(active_channels.map((ch) => ch.channel_id));

          // Update isActive status for each channel
          messageType.channels = messageType.channels.map((ch) => ({
            ...ch,
            is_active: activeChannelIds.has(ch.channel_id),
          }));

          messageType.loadingStatus = LoadingStatus.SUCCEEDED;
        }
      }
    },
  },
});

export const {
  setPersonalDetails,
  setCompanyDetails,
  setBillingDetails,
  setJobsiteDetails,
  addJobsiteLocation,
  setTeamDetails,
  setNotificationDetails,
  setMessageSettings,
  setPersonalDetailsLoadingStatus,
  setCompanyDetailsLoadingStatus,
  setBillingDetailsLoadingStatus,
  setJobsiteDetailsLoadingStatus,
  setJobsiteDetailsHasData,
  setJobsiteDetailsAllDataFetched,
  updateJobsiteDataStatus,
  setTeamDetailsLoadingStatus,
  setNotificationDetailsLoadingStatus,
  setMessageSettingsLoadingStatus,
  setMessageTypeLoadingStatusById,
  updateMessageTypeChannelsById,
  openPersonalDetailsUpdateModal,
  closePersonalDetailsUpdateModal,
} = profileSlice.actions;

export const loadPersonalDetails = createAction<ProfileDataLoadPayload>(
  'profile/loadPersonalDetails',
);
export const loadCompanyDetails = createAction<ProfileDataLoadPayload>(
  'profile/loadCompanyDetails',
);
export const loadBillingDetails = createAction<ProfileDataLoadPayload>(
  'profile/loadBillingDetails',
);
export const loadJobsiteDetails = createAction<JobsitesDataLoadPayload>(
  'profile/loadJobsiteDetails',
);
export const addJobsiteById = createAction<AddJobsiteByIdPayload>('profile/addJobsiteById');
export const loadTeamDetails = createAction<ProfileDataLoadPayload>('profile/loadTeamDetails');
export const loadNotificationDetails = createAction<ProfileDataLoadPayload>(
  'profile/loadNotificationDetails',
);
export const loadMessageSettings = createAction<ProfileDataLoadPayload>(
  'profile/loadMessageSettings',
);
export const updatedPersonalDetails = createAction('profile/updatedPersonalDetails');

export default profileSlice.reducer;
